'use client'

import {
    CHAT_READ_CHANGED_EVENT,
    getChatByCompany,
    getMyDepartments,
    MyDepartment,
} from '@/lib/api/atendimento/chat'
import {
    ChatFolder,
    getChatFolders,
    createChatFolder,
    deleteChatFolder,
} from '@/lib/api/atendimento/chat-folders'
import ChatActionsMenu from '@/components/chat-actions-menu'
import ChatFilterPanel from '@/components/atendimento/chat-filters/chat-filter-panel'
import {
    toQueryParams,
    useChatFilters,
    useChatFiltersMemory,
} from '@/components/atendimento/chat-filters/chat-filters'
import SearchableSelect from '@/components/searchable-select'
import { Chat, ChatStatus } from '@/lib/api/common/interfaces/message.interface'
import ChatContactLabel from '@/components/atendimento/ChatContactLabel'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { HighlightedText } from '@/components/atendimento/highlighted-text'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { SocketContext } from '@/lib/providers/socket-provider'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useAuth } from '@/store/hooks'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ChatChannelAvatarBadge } from '@/components/atendimento/chat-channel-icon'
import {
    MdAdd,
    MdChat,
    MdBusiness,
    MdClose,
    MdCreateNewFolder,
    MdDelete,
    MdFolder,
    MdOutlineMarkChatRead,
    MdOutlineMarkChatUnread,
    MdPersonSearch,
    MdSearch,
    MdSupportAgent,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import ConfirmModal from '@/components/confirm-modal'
import StartChatModal from '@/pages/admin/atendimento/atendimentos/start-chat-modal'

type VisibilityScope = 'mine' | 'triage' | 'all'

export interface ChatListPanelProps {
    /** Conversa destacada na lista. */
    activeChatId: number | null
    /** Quem decide o que fazer ao escolher: a tela navega, o dock sobrepõe. */
    onSelectChat: (chatId: number) => void
    /**
     * Onde mora o recorte da lista. `url` deixa filtro e página no endereço —
     * quem filtra, abre uma conversa e volta não perde nada, e o link serve para
     * mandar ao colega. `memory` é para o dock, que vive sobre telas de outros
     * módulos e não pode escrever na URL delas.
     */
    filterStorage?: 'url' | 'memory'
    /** Cabeçalho da lista. O dock troca por um título próprio, com o botão de fechar. */
    header?: React.ReactNode
    className?: string
}

/** Quem escreveu a mensagem encontrada pela busca. */
const MESSAGE_MATCH_AUTHOR: Record<'user' | 'company' | 'assistant', string> = {
    user: 'Contato',
    company: 'Equipe',
    assistant: 'IA',
}

/**
 * A lista de conversas do atendimento.
 *
 * Saiu de dentro da página para poder aparecer em dois lugares com o MESMO
 * comportamento: a tela cheia de atendimentos e o dock acoplado ao menu. O que
 * muda entre os dois é só onde o recorte é guardado e o que acontece ao escolher
 * uma conversa — tudo o mais (filtros salvos, pastas, setor, escopo, paginação,
 * tempo real) é o mesmo código, para as duas não divergirem com o tempo.
 */
export default function ChatListPanel({
    activeChatId,
    onSelectChat,
    filterStorage = 'url',
    header,
    className = '',
}: ChatListPanelProps) {
    const auth = useAuth()
    const user = auth.user
    const isEmployee = auth.isEmployee
    const { socket, isConnected } = useContext(SocketContext)

    // Os dois controladores são criados sempre (hook não pode ser condicional) e
    // só um é usado. Ambos são baratos: um lê a URL, o outro um estado local.
    const urlControl = useChatFilters()
    const memoryControl = useChatFiltersMemory()
    const control = filterStorage === 'url' ? urlControl : memoryControl
    const { filters: urlFilters, patchFilters, setFilters, clearPanelFilters } = control

    const [searchInput, setSearchInput] = useState(urlFilters.search ?? '')
    const [showSearch, setShowSearch] = useState(!!urlFilters.search)
    const [showStartModal, setShowStartModal] = useState(false)
    const statusFilter: ChatStatus | undefined =
        urlFilters.status === 'ALL'
            ? undefined
            : ((urlFilters.status as ChatStatus | undefined) ?? ChatStatus.OPEN)
    const setStatusFilter = (status: ChatStatus | undefined) =>
        patchFilters({ status: status ?? 'ALL' })
    const visibility: VisibilityScope = urlFilters.scope ?? (isEmployee ? 'mine' : 'all')
    const setVisibility = (scope: VisibilityScope) => patchFilters({ scope })
    const [folders, setFolders] = useState<ChatFolder[]>([])
    const selectedFolderId = urlFilters.folderId ?? null
    const setSelectedFolderId = (folderId: number | null) =>
        patchFilters({ folderId: folderId ?? undefined })
    // Setor: só os que o usuário é membro. O filtro estreita a lista; o que
    // ele NÃO pode ver já não vem do backend, então isto é conveniência, não
    // segurança.
    const [myDepartments, setMyDepartments] = useState<MyDepartment[]>([])
    const selectedDepartmentId = urlFilters.departmentId ?? null
    const setSelectedDepartmentId = (departmentId: number | null) =>
        patchFilters({ departmentId: departmentId ?? undefined })
    const [showDeleteFolder, setShowDeleteFolder] = useState(false)
    const [deletingFolder, setDeletingFolder] = useState(false)
    const [creatingFolder, setCreatingFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [savingFolder, setSavingFolder] = useState(false)

    const debouncedSearch = useDebounce(searchInput, 300)

    // A busca é digitada e só depois vai para o recorte — escrever a cada tecla
    // encheria o histórico do navegador de estados intermediários.
    useEffect(() => {
        const current = urlFilters.search ?? ''
        if (current !== debouncedSearch) {
            patchFilters({ search: debouncedSearch || undefined })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch])

    const loadFolders = useCallback(async () => {
        const res = await getChatFolders()
        if (res.success && Array.isArray(res.result)) {
            setFolders(res.result)
        }
    }, [])

    useEffect(() => {
        loadFolders()
    }, [loadFolders])

    useEffect(() => {
        getMyDepartments()
            .then(response => {
                if (response.success && Array.isArray(response.result)) {
                    setMyDepartments(response.result)
                }
            })
            .catch(() => {
                // Sem a lista, o seletor some e a listagem segue no padrão
                // (todos os setores do usuário). Não vale interromper a tela.
            })
    }, [])

    const selectedFolder = folders.find(f => f.id === selectedFolderId) || null
    const selectedDepartment =
        myDepartments.find(d => d.id === selectedDepartmentId) || null

    /**
     * Dropdown do DaisyUI só fecha quando o gatilho perde o foco — clicar num
     * item não basta, e o menu ficava aberto por cima da lista.
     */
    const pickDepartment = (departmentId: number | null) => {
        setSelectedDepartmentId(departmentId)
        const focused = document.activeElement as HTMLElement | null
        if (focused) focused.blur()
    }

    const folderOptions = useMemo(
        () => [
            { value: '', label: 'Todos os atendimentos' },
            ...folders.map(f => ({
                value: String(f.id),
                label: f.name,
                badge:
                    typeof f._count?.items === 'number'
                        ? String(f._count.items)
                        : undefined,
            })),
        ],
        [folders]
    )

    const handleCreateFolder = async () => {
        const name = newFolderName.trim()
        if (!name) return
        setSavingFolder(true)
        try {
            const res = await createChatFolder({ name })
            if (res.success && res.result) {
                toast.success('Pasta criada')
                setNewFolderName('')
                setCreatingFolder(false)
                await loadFolders()
                setSelectedFolderId(res.result.id)
            } else {
                toast.error(res.error || 'Erro ao criar pasta')
            }
        } finally {
            setSavingFolder(false)
        }
    }

    const handleConfirmDeleteFolder = async () => {
        if (!selectedFolderId) return
        setDeletingFolder(true)
        try {
            const res = await deleteChatFolder(selectedFolderId)
            if (res.success) {
                toast.success('Pasta excluída')
                setShowDeleteFolder(false)
                setSelectedFolderId(null)
                await loadFolders()
            } else {
                toast.error(res.error || 'Erro ao excluir pasta')
            }
        } finally {
            setDeletingFolder(false)
        }
    }

    const filters = useMemo(() => {
        const base: any = {
            ...toQueryParams(urlFilters),
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
        }
        // Pasta e escopo não convivem: dentro da pasta, o que vale é o que o
        // próprio usuário arquivou lá.
        if (selectedFolderId) {
            base.folderId = selectedFolderId
            delete base.scope
        } else if (isEmployee) {
            // O backend resolve o escopo: 'mine' (posse pela carteira do
            // cliente, lead ou deal), 'triage' (sem dono) e 'all'. O filtro de
            // SETOR é separado e vale para todos, inclusive gestores.
            base.scope = visibility
        } else {
            delete base.scope
        }
        return base
    }, [
        urlFilters,
        debouncedSearch,
        statusFilter,
        visibility,
        isEmployee,
        selectedFolderId,
    ])

    const fetchFn = useCallback((params: any) => {
        return getChatByCompany(user!.companyId!, params)
    }, [user?.companyId])

    // A página também só vale na URL na tela cheia; no dock ela é local.
    const [urlPage, setUrlPage] = useUrlPage()
    const [memoryPage, setMemoryPage] = useState(1)
    const pageSource = filterStorage === 'url' ? urlPage : memoryPage
    const onPageChange = filterStorage === 'url' ? setUrlPage : setMemoryPage

    const { items, loading, page, limit, total, setPage, reload, reloadSilent } = usePaginatedPage<Chat, typeof filters>({
        fetchFn,
        filters,
        limit: 30,
        enabled: !!user?.companyId,
        page: pageSource,
        onPageChange,
    })

    // Socket events
    useEffect(() => {
        if (!socket || !isConnected || !user?.companyId) return

        // Reload silencioso (sem spinner) — evita o "reload" visível da lista a
        // cada mensagem/atualização recebida via socket.
        const handleNewMessage = (data: any) => {
            if (data.messageId) reloadSilent()
        }
        const handleMessageClosed = () => reloadSilent()
        const handleMessageReopened = () => reloadSilent()
        const handleMessageUpdate = () => reloadSilent()

        socket.on(`new-message-${user.companyId}`, handleNewMessage)
        socket.on(`message-closed-${user.companyId}`, handleMessageClosed)
        socket.on(`message-reopened-${user.companyId}`, handleMessageReopened)
        socket.on(`message-update-${user.companyId}`, handleMessageUpdate)
        // Contato que ainda não escolheu a unidade fica sem `companyId`: o
        // canal por empresa não alcança esse chat, e a mensagem só aparecia
        // depois de recarregar a página.
        if (user.organizationId) {
            socket.on(`new-message-o-${user.organizationId}`, handleNewMessage)
            // Mesma razão para a ATUALIZAÇÃO: um contato transferido para esta pessoa
            // pode estar numa conversa de outra unidade, e o canal da empresa não a
            // alcança. Sem isto, a conversa recém-recebida só aparecia ao recarregar.
            socket.on(`message-update-o-${user.organizationId}`, handleMessageUpdate)
        }

        return () => {
            socket.off(`new-message-${user.companyId}`, handleNewMessage)
            socket.off(`message-closed-${user.companyId}`, handleMessageClosed)
            socket.off(`message-reopened-${user.companyId}`, handleMessageReopened)
            socket.off(`message-update-${user.companyId}`, handleMessageUpdate)
            if (user.organizationId) {
                socket.off(`new-message-o-${user.organizationId}`, handleNewMessage)
                socket.off(`message-update-o-${user.organizationId}`, handleMessageUpdate)
            }
        }
    }, [socket, isConnected, user?.companyId, user?.organizationId, reloadSilent])

    // A leitura é por pessoa: abrir uma conversa não avisa a empresa pelo socket, então
    // a lista de quem leu se atualiza por este evento local.
    useEffect(() => {
        const handler = () => reloadSilent()
        window.addEventListener(CHAT_READ_CHANGED_EVENT, handler)
        return () => window.removeEventListener(CHAT_READ_CHANGED_EVENT, handler)
    }, [reloadSilent])

    // Recarrega quando um chat é movido para pasta pela tela de detalhe.
    useEffect(() => {
        const handler = () => {
            reloadSilent()
            loadFolders()
        }
        window.addEventListener('chat-folders-changed', handler)
        return () => window.removeEventListener('chat-folders-changed', handler)
    }, [reloadSilent, loadFolders])

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .substring(0, 2)
            .toUpperCase()
    }

    const getAvatarColor = (id: number) => {
        const colors = [
            'from-violet-500 to-purple-600',
            'from-blue-500 to-cyan-600',
            'from-emerald-500 to-teal-600',
            'from-orange-500 to-amber-600',
            'from-pink-500 to-rose-600',
            'from-indigo-500 to-blue-600',
        ]
        return colors[id % colors.length]
    }

    return (
        <div className={`flex flex-col bg-base-100 ${className}`}>
            {/* Header */}
            <div className="flex-shrink-0 p-3 border-b border-base-300/30">
                <div className="flex items-center justify-between mb-2">
                    {header ?? (
                        <div className="flex items-center gap-2">
                            <MdSupportAgent className="text-lg text-secondary" />
                            <h1 className="text-base font-bold text-base-content">Atendimentos</h1>
                            <span className="text-xs text-base-content/50">({total})</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <ChatFilterPanel
                            filters={urlFilters}
                            onChange={patchFilters}
                            onApplyPreset={setFilters}
                            onClear={clearPanelFilters}
                            organizationId={user?.organizationId}
                        />
                        <button
                            onClick={() => setShowStartModal(true)}
                            className="btn btn-sm btn-secondary btn-circle"
                            title="Novo atendimento"
                        >
                            <MdAdd className="text-lg" />
                        </button>
                        <button
                            onClick={() => setShowSearch(!showSearch)}
                            className="btn btn-sm btn-ghost btn-circle"
                        >
                            {showSearch ? <MdClose className="text-base" /> : <MdSearch className="text-lg" />}
                        </button>
                    </div>
                </div>

                {/* Busca */}
                {showSearch && (
                    <div className="mb-2">
                        <input
                            type="text"
                            placeholder="Nome, telefone ou termo da conversa"
                            className="input input-bordered input-sm w-full text-sm"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            autoFocus
                        />
                    </div>
                )}

                {/* Status Filter */}
                <div className="flex gap-1">
                    {[
                        { status: undefined, label: 'Todos', icon: <MdChat className="text-xs" />, active: '', inactive: 'btn-ghost' },
                        { status: ChatStatus.OPEN, label: 'Abertos', icon: <MdOutlineMarkChatUnread className="text-xs" />, active: 'btn-warning', inactive: 'btn-ghost' },
                        { status: ChatStatus.CLOSED, label: 'Fechados', icon: <MdOutlineMarkChatRead className="text-xs" />, active: 'btn-success', inactive: 'btn-ghost' },
                    ].map(f => (
                        <button
                            key={f.label}
                            onClick={() => setStatusFilter(f.status)}
                            className={`btn btn-xs flex-1 gap-1 ${statusFilter === f.status ? (f.active || 'btn-secondary') : f.inactive}`}
                        >
                            {f.icon}
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Pastas pessoais */}
                <div className="flex items-center gap-1 mt-2">
                    <MdFolder className="text-base text-base-content/60 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <SearchableSelect
                            options={folderOptions}
                            value={selectedFolderId ? String(selectedFolderId) : ''}
                            onChange={value =>
                                setSelectedFolderId(value ? Number(value) : null)
                            }
                            placeholder="Pastas"
                            searchPlaceholder="Buscar pasta..."
                            noResultsText="Nenhuma pasta"
                        />
                    </div>
                    {creatingFolder ? (
                        <>
                            <input
                                type="text"
                                autoFocus
                                value={newFolderName}
                                onChange={e => setNewFolderName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') handleCreateFolder()
                                    if (e.key === 'Escape') {
                                        setCreatingFolder(false)
                                        setNewFolderName('')
                                    }
                                }}
                                placeholder="Nome da pasta"
                                maxLength={60}
                                className="input input-bordered input-xs w-32"
                            />
                            <button
                                onClick={handleCreateFolder}
                                disabled={savingFolder || !newFolderName.trim()}
                                className="btn btn-xs btn-secondary"
                            >
                                Criar
                            </button>
                            <button
                                onClick={() => {
                                    setCreatingFolder(false)
                                    setNewFolderName('')
                                }}
                                className="btn btn-xs btn-ghost btn-circle"
                            >
                                <MdClose className="text-xs" />
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => setCreatingFolder(true)}
                            className="btn btn-xs btn-ghost btn-circle flex-shrink-0"
                            title="Nova pasta"
                        >
                            <MdCreateNewFolder className="text-sm" />
                        </button>
                    )}
                    {selectedFolder && !creatingFolder && (
                        <button
                            onClick={() => setShowDeleteFolder(true)}
                            className="btn btn-xs btn-ghost btn-circle text-error flex-shrink-0"
                            title="Excluir pasta"
                        >
                            <MdDelete className="text-xs" />
                        </button>
                    )}

                    {/* Setor: só ícone. A coluna é estreita e o nome do
                        setor escolhido já aparece em cada item da lista. */}
                    {myDepartments.length > 0 && !creatingFolder && (
                        <div className="dropdown dropdown-end flex-shrink-0">
                            <div
                                tabIndex={0}
                                role="button"
                                title={
                                    selectedDepartment
                                        ? `Setor: ${selectedDepartment.name}`
                                        : 'Filtrar por setor'
                                }
                                className={`btn btn-xs btn-circle ${
                                    selectedDepartmentId
                                        ? 'btn-secondary'
                                        : 'btn-ghost'
                                }`}
                            >
                                <MdBusiness className="text-sm" />
                            </div>
                            <ul
                                tabIndex={0}
                                className="dropdown-content menu z-30 mt-1 max-h-80 w-56 overflow-y-auto rounded-box border border-base-200 bg-base-100 p-2 shadow-lg"
                            >
                                <li className="menu-title px-3 py-1">
                                    <span className="text-xs">Setor</span>
                                </li>
                                <li>
                                    <a
                                        className={
                                            selectedDepartmentId === null
                                                ? 'active'
                                                : ''
                                        }
                                        onClick={() => pickDepartment(null)}
                                    >
                                        Todos os meus setores
                                    </a>
                                </li>
                                {myDepartments.map(department => (
                                    <li key={department.id}>
                                        <a
                                            className={
                                                selectedDepartmentId ===
                                                department.id
                                                    ? 'active'
                                                    : ''
                                            }
                                            onClick={() =>
                                                pickDepartment(department.id)
                                            }
                                        >
                                            {department.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Visibility Filter (só para employees) */}
                {isEmployee && (
                    <div className="flex gap-1 mt-2">
                        {[
                            { scope: 'mine' as const, label: 'Meus' },
                            { scope: 'triage' as const, label: 'Triagem' },
                            { scope: 'all' as const, label: 'Todos' },
                        ].map(v => (
                            <button
                                key={v.scope}
                                onClick={() => setVisibility(v.scope)}
                                className={`btn btn-xs flex-1 ${visibility === v.scope ? 'btn-secondary' : 'btn-ghost'}`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <span className="loading loading-spinner loading-md text-secondary" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                        <MdSupportAgent className="text-4xl text-base-content/20 mb-2" />
                        <p className="text-sm text-base-content/50">
                            {searchInput ? 'Nenhum resultado' : 'Nenhum atendimento'}
                        </p>
                    </div>
                ) : (
                    <>
                        {items.map(chat => {
                            const isOpen = chat.status === ChatStatus.OPEN
                            const isActive = activeChatId === chat.id
                            // A conversa aberta agora já está sendo vista: sem contador nela
                            const unread = isActive ? 0 : (chat.unreadCount ?? 0)

                            return (
                                <div
                                    key={chat.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onSelectChat(chat.id)}
                                    className={`w-full cursor-pointer text-left px-3 py-2.5 border-b border-base-300/20 hover:bg-base-200/50 transition-colors ${isActive ? 'bg-secondary/5 border-l-2 border-l-primary' : unread > 0 ? 'bg-success/10' : ''} ${!isOpen && unread === 0 ? 'opacity-60' : ''}`}
                                >
                                    <div className="flex items-center gap-2.5">
                                        {/* Avatar */}
                                        <div className="relative flex-shrink-0">
                                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(chat.id)} flex items-center justify-center`}>
                                                <span className="text-xs font-bold text-white">
                                                    {getInitials(chat.name)}
                                                </span>
                                            </div>
                                            {isOpen && (
                                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-base-100" />
                                            )}
                                            <ChatChannelAvatarBadge channel={chat.channel} />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-1">
                                                <span className={`text-sm truncate text-base-content ${unread > 0 ? 'font-bold' : 'font-semibold'}`}>
                                                    {chat.name}
                                                </span>
                                                <span className={`text-[10px] flex-shrink-0 ${unread > 0 ? 'text-success font-semibold' : 'text-base-content/40'}`}>
                                                    {formatDistanceToNow(new Date(chat.lastMessageAt ?? chat.updatedAt), { addSuffix: false, locale: ptBR })}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-base-content/50 truncate flex-1">
                                                    <ChatContactLabel
                                                        channel={chat.channel}
                                                        phoneNumber={chat.phoneNumber}
                                                    />
                                                </span>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    {unread > 0 && (
                                                        <span
                                                            className="min-w-[18px] h-[18px] px-1 rounded-full bg-success text-success-content text-[10px] font-bold flex items-center justify-center"
                                                            title={`${unread} ${unread === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`}
                                                        >
                                                            {unread > 99 ? '99+' : unread}
                                                        </span>
                                                    )}
                                                    {!isOpen && (
                                                        <span className="badge badge-xs bg-base-300 text-base-content/60 border-0" title="Atendimento fechado">
                                                            Fechado
                                                        </span>
                                                    )}
                                                    {chat.waitingForHuman && (
                                                        <span className="w-1.5 h-1.5 rounded-full bg-warning" title="Aguardando humano" />
                                                    )}
                                                    {chat.leadId && !chat.clientId && (
                                                        <MdPersonSearch className="text-[10px] text-info" title="Lead" />
                                                    )}
                                                    {chat.department && (
                                                        <span className="text-[10px] text-secondary truncate max-w-[60px]">
                                                            {chat.department.name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {chat.messageMatch && (
                                                <p
                                                    className="mt-1 text-xs text-base-content/70 line-clamp-2 break-words"
                                                    title="Trecho da conversa que contém o termo buscado"
                                                >
                                                    <span className="font-medium text-base-content/50">
                                                        {MESSAGE_MATCH_AUTHOR[chat.messageMatch.role]}:{' '}
                                                    </span>
                                                    <HighlightedText
                                                        text={chat.messageMatch.snippet}
                                                        term={debouncedSearch}
                                                    />
                                                </p>
                                            )}
                                        </div>

                                        <ChatActionsMenu
                                            chatId={chat.id}
                                            folders={folders}
                                            currentFolderId={selectedFolderId}
                                            showMarkUnread
                                            alreadyUnread={
                                                (chat.unreadCount ?? 0) > 0
                                            }
                                            onChanged={() => {
                                                reload()
                                                loadFolders()
                                            }}
                                            buttonClassName="btn btn-ghost btn-xs btn-circle flex-shrink-0"
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </>
                )}
            </div>

            {total > limit && (
                <div className="flex-shrink-0 border-t border-base-300/30 px-2">
                    <PaginationControls
                        page={page}
                        limit={limit}
                        total={total}
                        loading={loading}
                        onPageChange={setPage}
                        siblingCount={0}
                        boundaryCount={1}
                        hideEdgeJumps
                        compact
                    />
                </div>
            )}

            <StartChatModal
                isOpen={showStartModal}
                onClose={() => setShowStartModal(false)}
                onStarted={(chatId) => {
                    reload()
                    onSelectChat(chatId)
                }}
            />

            <ConfirmModal
                isOpen={showDeleteFolder}
                onClose={() => setShowDeleteFolder(false)}
                onConfirm={handleConfirmDeleteFolder}
                title="Excluir pasta?"
                message={`Tem certeza de que deseja excluir a pasta "${selectedFolder?.name ?? ''}"? Os atendimentos arquivados nela voltam para a listagem.`}
                confirmButtonText="Excluir"
                loading={deletingFolder}
                confirmButtonClass="btn btn-error"
            />
        </div>
    )
}
